// Theme keys must match the backend allow-list (GET /api/themes) exactly.
//
// Per-theme tokens:
//   name, gradient, text, isDark          page look
//   linkRadius, linkStyle                 link card shape: filled | outline | soft | shadow | glass
// Optional tokens (all fall back to sensible values derived from isDark/text):
//   surface       card background for filled / soft / shadow
//   borderColor   card border color (defaults per style)
//   borderWidth   card border width (default "1px")
//   shadowColor   hard-shadow color for the shadow style (default: text)
//   glow          glow color for the outline style
//   accent        icon color inside the icon wrapper
//   iconBg        icon wrapper background
//   font          CSS font-family stack, loaded via fontUrl
//   fontUrl       Google Fonts stylesheet URL for `font`
export const themes = [
  // ---- Existing themes (stored by users; never rename or remove) ----
  {
    name: "default",
    gradient: "linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)",
    text: "#0f172a",
    isDark: false,
    linkRadius: "16px",
    linkStyle: "filled",
  },
  {
    name: "midnight",
    gradient: "linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #312e81 100%)",
    text: "#f8fafc",
    isDark: true,
    linkRadius: "16px",
    linkStyle: "glass",
  },
  {
    name: "violet",
    gradient: "linear-gradient(135deg, #7c3aed 0%, #c026d3 100%)",
    text: "#ffffff",
    isDark: true,
    linkRadius: "16px",
    linkStyle: "glass",
  },
  {
    name: "sunset",
    gradient: "linear-gradient(135deg, #f97316 0%, #ef4444 55%, #ec4899 100%)",
    text: "#ffffff",
    isDark: true,
    linkRadius: "16px",
    linkStyle: "glass",
  },
  {
    name: "ocean",
    gradient: "linear-gradient(135deg, #0ea5e9 0%, #14b8a6 100%)",
    text: "#ffffff",
    isDark: true,
    linkRadius: "16px",
    linkStyle: "glass",
  },
  {
    name: "forest",
    gradient: "linear-gradient(135deg, #22c55e 0%, #15803d 60%, #064e3b 100%)",
    text: "#ffffff",
    isDark: true,
    linkRadius: "16px",
    linkStyle: "glass",
  },
  {
    name: "rose",
    gradient: "linear-gradient(135deg, #fecdd3 0%, #fda4af 60%, #fb7185 100%)",
    text: "#4c0519",
    isDark: false,
    linkRadius: "16px",
    linkStyle: "filled",
  },

  // ---- New themes ----
  {
    name: "paper",
    gradient: "linear-gradient(135deg, #faf7f0 0%, #efe9da 100%)",
    text: "#1c1917",
    isDark: false,
    linkRadius: "0px",
    linkStyle: "outline",
    borderWidth: "1px",
    accent: "#1c1917",
    font: "'Lora', Georgia, serif",
    fontUrl: "https://fonts.googleapis.com/css2?family=Lora:wght@400;600&display=swap",
  },
  {
    name: "brutal",
    gradient: "linear-gradient(135deg, #fde047 0%, #facc15 100%)",
    text: "#111111",
    isDark: false,
    linkRadius: "4px",
    linkStyle: "shadow",
    surface: "#ffffff",
    borderWidth: "3px",
    accent: "#db2777",
    font: "'Space Grotesk', system-ui, sans-serif",
    fontUrl: "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600;700&display=swap",
  },
  {
    name: "neon",
    gradient: "linear-gradient(135deg, #05010f 0%, #12042b 60%, #1a0536 100%)",
    text: "#e0f2fe",
    isDark: true,
    linkRadius: "9999px",
    linkStyle: "outline",
    borderColor: "#22d3ee",
    borderWidth: "1.5px",
    glow: "rgba(34, 211, 238, 0.55)",
    accent: "#22d3ee",
    uniformIcons: true,
    iconBg: "rgba(255, 255, 255, 0.06)",
  },
  {
    name: "candy",
    gradient: "linear-gradient(135deg, #fbcfe8 0%, #bfdbfe 100%)",
    text: "#4c1d95",
    isDark: false,
    linkRadius: "9999px",
    linkStyle: "soft",
    surface: "rgba(255, 255, 255, 0.68)",
    accent: "#db2777",
  },
  {
    name: "terminal",
    gradient: "linear-gradient(180deg, #000000 0%, #03120a 100%)",
    text: "#4ade80",
    isDark: true,
    linkRadius: "0px",
    linkStyle: "outline",
    borderWidth: "1px",
    accent: "#4ade80",
    uniformIcons: true,
    iconBg: "transparent",
    font: "'JetBrains Mono', ui-monospace, 'Courier New', monospace",
    fontUrl: "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600&display=swap",
  },
  {
    name: "sakura",
    gradient: "linear-gradient(160deg, #fbcfe8 0%, #fdf2f8 55%, #ffffff 100%)",
    text: "#831843",
    isDark: false,
    linkRadius: "28px",
    linkStyle: "soft",
    surface: "rgba(244, 114, 182, 0.16)",
    accent: "#be185d",
  },
  {
    name: "mocha",
    gradient: "linear-gradient(135deg, #3b2418 0%, #5a3a28 100%)",
    text: "#f5e9d8",
    isDark: true,
    linkRadius: "14px",
    linkStyle: "filled",
    surface: "#6b4530",
    borderColor: "#8a5d43",
    accent: "#6b4530",
    iconBg: "#f5e9d8",
  },
  {
    name: "glacier",
    gradient: "linear-gradient(135deg, #bae6fd 0%, #e0f2fe 55%, #f0f9ff 100%)",
    text: "#0c4a6e",
    isDark: false,
    linkRadius: "14px",
    linkStyle: "glass",
    accent: "#0369a1",
  },
  {
    name: "aurora",
    gradient: "linear-gradient(135deg, #022c22 0%, #065f46 40%, #4c1d95 100%)",
    text: "#f0fdf4",
    isDark: true,
    linkRadius: "24px",
    linkStyle: "glass",
  },
  {
    name: "retro",
    gradient: "linear-gradient(160deg, #fdf0d5 0%, #fbd9a5 70%, #f6b26b 100%)",
    text: "#3b2a1a",
    isDark: false,
    linkRadius: "8px",
    linkStyle: "shadow",
    surface: "#fffaf0",
    borderWidth: "2px",
    shadowColor: "#0f766e",
    accent: "#c2410c",
    font: "'Space Mono', ui-monospace, monospace",
    fontUrl: "https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&display=swap",
  },
  {
    name: "mono",
    gradient: "linear-gradient(135deg, #000000 0%, #000000 100%)",
    text: "#ffffff",
    isDark: true,
    linkRadius: "0px",
    linkStyle: "shadow",
    surface: "#000000",
    borderWidth: "2px",
    shadowColor: "#ffffff",
    accent: "#000000",
    iconBg: "#ffffff",
  },
  {
    name: "dusk",
    gradient: "linear-gradient(160deg, #4c1d95 0%, #be185d 50%, #c2410c 100%)",
    text: "#ffffff",
    isDark: true,
    linkRadius: "9999px",
    linkStyle: "soft",
    surface: "rgba(0, 0, 0, 0.24)",
  },
];

const SMALL_SHADOW = "0 1px 2px 0 rgb(0 0 0 / 0.05)";

// Icon wrappers were circles inside 16px cards; keep that for roundish cards and
// follow the card radius for sharper ones so they don't look mismatched.
function iconRadius(linkRadius) {
  const px = parseFloat(linkRadius);
  return Number.isNaN(px) || px >= 16 ? "9999px" : linkRadius;
}

// Derives the link card styling for a theme from its linkStyle/linkRadius tokens,
// so markup never needs per-theme conditionals.
export function getThemeSurface(themeItem) {
  const t = themeItem || themes[0];
  const isDark = Boolean(t.isDark);
  const radius = t.linkRadius || "16px";
  const style = t.linkStyle || (isDark ? "glass" : "filled");
  const bw = t.borderWidth || "1px";
  const text = t.text || (isDark ? "#ffffff" : "#0f172a");

  const iconWrap = {
    background: t.iconBg || (isDark ? "rgba(255, 255, 255, 0.85)" : "#ffffff"),
    borderRadius: iconRadius(radius),
  };
  const base = {
    mutedOpacity: isDark ? 0.75 : 0.6,
    iconWrap,
    iconColor: t.accent || "#2a5ce6",
    // Themes where every link icon should share the accent instead of its brand colour.
    uniformIcons: Boolean(t.uniformIcons),
  };
  const card = { borderRadius: radius, boxShadow: "none" };

  switch (style) {
    case "outline":
      Object.assign(card, {
        background: "transparent",
        border: `${bw} solid ${t.borderColor || text}`,
      });
      if (t.glow) {
        card.boxShadow = `0 0 12px ${t.glow}, inset 0 0 8px ${t.glow}`;
      }
      break;
    case "soft":
      Object.assign(card, {
        background: t.surface || (isDark ? "rgba(255, 255, 255, 0.18)" : "rgba(255, 255, 255, 0.6)"),
        border: "none",
      });
      break;
    case "shadow":
      Object.assign(card, {
        background: t.surface || (isDark ? "rgba(0, 0, 0, 0.35)" : "#ffffff"),
        border: `${bw} solid ${t.borderColor || text}`,
        boxShadow: `4px 4px 0 ${t.shadowColor || text}`,
      });
      break;
    case "glass":
      if (isDark) {
        Object.assign(card, {
          background: "rgba(255, 255, 255, 0.14)",
          border: `1px solid ${t.borderColor || "rgba(255, 255, 255, 0.28)"}`,
          boxShadow: SMALL_SHADOW,
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        });
      } else {
        Object.assign(card, {
          background: "rgba(255, 255, 255, 0.5)",
          border: `1px solid ${t.borderColor || "rgba(255, 255, 255, 0.9)"}`,
          boxShadow: "0 4px 14px rgba(15, 23, 42, 0.1)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        });
      }
      break;
    case "filled":
    default:
      Object.assign(card, {
        background: t.surface || (isDark ? "rgba(255, 255, 255, 0.14)" : "#ffffff"),
        border: `1px solid ${t.borderColor || (isDark ? "rgba(255, 255, 255, 0.28)" : "#e2e8f0")}`,
        boxShadow: SMALL_SHADOW,
      });
  }
  return { ...base, card };
}

export function getActiveTheme(themeName) {
  return themes.find((item) => item.name === themeName) || themes[0];
}

// Loads a theme's web font once (shared <link> per URL); no-op for themes without one.
export function loadThemeFont(themeItem) {
  const url = themeItem?.fontUrl;
  if (!url || typeof document === "undefined") return;
  const id = "theme-font-" + themeItem.name;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = url;
  document.head.appendChild(link);
}
